import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RequestMeetingCmd } from 'application/notification/command/request-meeting.cmd';
import { SendMeetingAlternativeCmd } from 'application/notification/command/send-meeting-alternative.cmd';
import { NotificationApiPort } from 'application/notification/port/notification-api.port';
import { DeleteNotificationsApiMutation } from 'infrastructure/graphql/messaging/notification/mutation/delete-notifications.mutation';
import { FetchTasksByIdsApiQuery } from 'task/query/fetch-tasks-by-ids.query';
import { UserSessionPort } from 'user/port/user-session.port';
import { FetchUsersByIdsApiQuery } from 'user/query/fetch-users-by-ids.query';

import { ApiAdapter } from '../../common/api.adpater';
import { NotificationOutput } from '../../graphql/messaging/notification/model/notification.output';
import { AcceptMeetingApiMutation } from '../../graphql/messaging/notification/mutation/accept-meeting.mutation';
import { CancelMeetingRequestApiMutation } from '../../graphql/messaging/notification/mutation/cancel-meeting-request.mutation';
import { DeleteNotificationApiMutation } from '../../graphql/messaging/notification/mutation/delete-notification.mutation';
import { RejectMeetingApiMutation } from '../../graphql/messaging/notification/mutation/reject-meeting.mutation';
import { RequestMeetingApiMutation } from '../../graphql/messaging/notification/mutation/request-meeting.mutation';
import { SendMeetingAlternativeApiMutation } from '../../graphql/messaging/notification/mutation/send-meeting-alternative.mutation';
import { FetchNotificationByIdApiQuery } from '../../graphql/messaging/notification/query/fetch-notification-by-id.query';
import { FetchNotificationsApiQuery } from '../../graphql/messaging/notification/query/fetch-notifications.query';

@Injectable()
export class NotificationApiAdapter
	extends ApiAdapter
	implements NotificationApiPort
{
	constructor(
		userSessionPort: UserSessionPort,
		translateService: TranslateService,
		private _fetchTasksByIdsApiQuery: FetchTasksByIdsApiQuery,
		private _fetchUsersByIdsApiQuery: FetchUsersByIdsApiQuery,
		private _acceptMeetingApiMutation: AcceptMeetingApiMutation,
		private _rejectMeetingApiMutation: RejectMeetingApiMutation,
		private _requestMeetingApiMutation: RequestMeetingApiMutation,
		private _fetchNotificationsApiQuery: FetchNotificationsApiQuery,
		private _deleteNotificationApiMutation: DeleteNotificationApiMutation,
		private _fetchNotificationByIdApiQuery: FetchNotificationByIdApiQuery,
		private _deleteNotificationsApiMutation: DeleteNotificationsApiMutation,
		private _cancelMeetingRequestApiMutation: CancelMeetingRequestApiMutation,
		private _sendMeetingAlternativeApiMutation: SendMeetingAlternativeApiMutation
	) {
		super(userSessionPort, translateService);
	}

	public acceptMeeting(id: string): Observable<string> {
		return this.mutate$(this._acceptMeetingApiMutation, { id }).pipe(
			map((notification) => notification.id)
		);
	}

	public cancelMeetingRequest(id: string): Observable<void> {
		return this.mutate$(this._cancelMeetingRequestApiMutation, { id });
	}

	public fetchNotification(id: string): Observable<NotificationOutput> {
		return this.fetch$(this._fetchNotificationByIdApiQuery, { id });
	}

	public fetchNotifications(): Observable<NotificationOutput[]> {
		return this.fetch$(this._fetchNotificationsApiQuery, {
			query: { offset: 0, chunckSize: 50 },
		}).pipe(map((page) => page.elements));
	}

	public markAllNotificationsRead(): Observable<string[]> {
		return this.mutate$(this._deleteNotificationsApiMutation);
	}

	public markNotificationRead(id: string): Observable<void> {
		return this.mutate$(this._deleteNotificationApiMutation, { id });
	}

	public rejectMeeting(id: string): Observable<void> {
		return this.mutate$(this._rejectMeetingApiMutation, { id });
	}

	public requestMeeting(cmd: RequestMeetingCmd): Observable<string> {
		return this.mutate$(this._requestMeetingApiMutation, {
			input: {
				coachId: cmd.coachId,
				endTimestamp: cmd.endDate.getTime(),
				startTimestamp: cmd.startDate.getTime(),
			},
		}).pipe(map((notification) => notification.id));
	}

	public sendMeetingAlternative(
		cmd: SendMeetingAlternativeCmd
	): Observable<string> {
		return this.mutate$(this._sendMeetingAlternativeApiMutation, {
			input: {
				notificationId: cmd.notificationId,
				endTimestamp: cmd.endDate.getTime(),
				startTimestamp: cmd.startDate.getTime(),
			},
		}).pipe(map((notification) => notification.id));
	}
}
